import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { notification } from "antd";
import { getCompanyId } from "@lhd/utility";
import { handleMessageError } from "@lhd/features/Apps/utils";

const deletingErrorToastSet = new Set<string>();
const getOrgErrorMessage = (
  rawError: any,
  fallback = "保存に失敗しました。",
): string => {
  if (Array.isArray(rawError)) {
    const firstString = rawError.find((item) => typeof item === "string");
    if (typeof firstString === "string" && firstString.length > 0) {
      return firstString;
    }

    const firstNestedMessage = rawError
      .flatMap((item: any) =>
        Array.isArray(item?.messages)
          ? item.messages.filter((m: any) => typeof m === "string")
          : [],
      )
      .find((msg: any) => typeof msg === "string");

    if (typeof firstNestedMessage === "string" && firstNestedMessage.length) {
      return firstNestedMessage;
    }
  }

  const parsed = handleMessageError(rawError, fallback);
  return typeof parsed === "string" && parsed.length > 0 ? parsed : fallback;
};

const notifyDeleteErrorOnce = (message: string) => {
  if (deletingErrorToastSet.has(message)) return;
  deletingErrorToastSet.add(message);
  notification["error"]({ message });
  setTimeout(() => {
    deletingErrorToastSet.delete(message);
  }, 1500);
};

export const useFetchOrgs = (
  page?: number,
  pageSize?: number,
  filterParams?: any,
  isIgnoreCall?: boolean,
) => {
  const companyId = getCompanyId();

  return useQuery(
    [QUERY_NAME.ORGS, page, pageSize, filterParams, companyId],
    () => {
      return axios({
        method: "get",
        url: SERVICES.ORGS,
        params: {
          ...(pageSize !== 0 && {
            page,
            per_page: pageSize,
          }),
          ...filterParams,
          "q[company_id_eq]": companyId,
          current_company_id: companyId,
        },
      }).then((res) => ({
        data: res.data as any[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !isIgnoreCall,
    },
  );
};
export const useFetchOrgsNode = (id: string) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.ORG_NODES, id, companyId],
    () =>
      axios({
        method: "get",
        url: SERVICES.ORG_NODES,
        params: {
          page: 1,
          per_page: 9999,
          offset: 0,
          "q[org_id_eq]": id,
          "q[company_id_eq]": companyId,
        },
      }).then((res) => ({
        data: res.data as any[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!id,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );
};

export const useFetchOrgsNodes = (orgIds: number[]) => {
  const companyId = getCompanyId();

  const cacheKey = [QUERY_NAME.ORG_NODES, orgIds.join("-")];

  return useQuery(
    cacheKey,
    () => {
      // Optimized: Fetch all orgs with their org_nodes in single API call instead of N calls
      return axios({
        method: "get",
        url: SERVICES.ORGS,
        params: {
          page: 1,
          per_page: 9999,
          offset: 0,
          "q[company_id_eq]": companyId,
          current_company_id: companyId,
        },
      }).then((res) => {
        // Build a map of org_id -> org_nodes
        const orgNodesMap: Record<number, any[]> = {};
        const orgsData = res.data as any[];

        orgsData.forEach((org) => {
          if (org.org_nodes && Array.isArray(org.org_nodes)) {
            orgNodesMap[org.id] = org.org_nodes;
          } else {
            orgNodesMap[org.id] = [];
          }
        });

        // Return org_nodes in the same order as orgIds
        return orgIds.map((orgId) => orgNodesMap[orgId] || []);
      });
    },
    {
      cacheTime: 3000,
      staleTime: 3000,
    },
  );
};

export const useUpdateOrgNodes = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload, deleteIds, branch, silent, orgId }: any) =>
      axios({
        method: "POST",
        url: `${SERVICES.ORG_NODES}/tree`,
        params: {
          company_id: companyId,
        },
        data: {
          org_nodes_data: {
            data: payload,
            delete_ids: deleteIds,
          },
        },
        // params: {
        //   model_name: "division",
        //   branch_id: branch === 0 ? null : branch,
        // },
      }),
    {
      onSuccess(data, variables: any) {
        // Only invalidate ORG_NODES query for the specific org being updated
        // This prevents unnecessary refetches of other orgs' nodes
        if (variables?.orgId) {
          queryClient.invalidateQueries(
            [QUERY_NAME.ORG_NODES, String(variables.orgId), companyId],
            {
              refetchType: "none",
            },
          );
        }
        // Keep DIVISIONS invalidation for backward compatibility
        queryClient.invalidateQueries([QUERY_NAME.DIVISIONS], {
          refetchType: "none",
        });
        // Only show notification if not silent
        if (!variables?.silent) {
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(error: any, variables: any) {
        if (variables?.silent) return;
        const msgError = getOrgErrorMessage(
          error?.response?.data?.msg || error?.response?.data?.error,
          "保存に失敗しました。",
        );
        notification["error"]({
          message: msgError || "保存に失敗しました。",
        });
      },
    },
  );
};

export const useDeleteOrg = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ orgId, silent }: any) =>
      axios({
        method: "delete",
        url: `${SERVICES.ORGS}/${orgId}`,
        params: {
          current_company_id: companyId,
        },
      }),
    {
      onSuccess(data, variables: any) {
        // Full refresh on delete: refetch orgs and org_nodes will auto-refetch due to orgIds change
        queryClient.invalidateQueries([QUERY_NAME.ORGS], {
          refetchType: "active",
        });
        if (!variables?.silent) {
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(error: any) {
        const backendMessage = getOrgErrorMessage(
          error?.response?.data?.msg || error?.response?.data?.error,
          "削除に失敗しました。",
        );
        notifyDeleteErrorOnce(backendMessage || "削除に失敗しました。");
      },
    },
  );
};

export const useDeleteMultipleOrgs = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ listIds, silent }: any) => {
      const formData = new FormData();
      listIds.forEach((id: any) => {
        formData.append(`delete_ids[]`, id);
      });
      formData.append("current_company_id", companyId.toString());
      return axios({
        method: "post",
        data: formData,
        url: `${SERVICES.ORGS}/delete_multiple`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(data, variables: any) {
        // Full refresh on delete multiple: refetch orgs and org_nodes will auto-refetch due to orgIds change
        queryClient.invalidateQueries([QUERY_NAME.ORGS], {
          refetchType: "active",
        });
        if (!variables?.silent) {
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(error: any) {
        const backendMessage = getOrgErrorMessage(
          error?.response?.data?.msg || error?.response?.data?.error,
          "削除に失敗しました。",
        );
        notifyDeleteErrorOnce(backendMessage || "削除に失敗しました。");
      },
    },
  );
};

export const useFetchOneOrg = (orgId: any) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.ORG, orgId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.ORGS}/${orgId}`,
        params: {
          current_company_id: companyId,
        },
      }).then((res) => res.data as any),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!orgId,
    },
  );
};

export const useSubmitOrg = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload, orgId, silent }: any) =>
      axios({
        method: orgId ? "patch" : "post",
        url: orgId ? `${SERVICES.ORGS}/${orgId}` : SERVICES.ORGS,
        data: { ...payload, company_id: companyId },
        params: {
          company_id: companyId,
          current_company_id: companyId,
        },
      }),
    {
      onSuccess(data, variables: any) {
        // Full refresh on add org: refetch orgs and org_nodes will auto-refetch due to orgIds change
        // Selective refresh on edit org: invalidate without auto-refetch to avoid unnecessary API calls
        const refetchType = variables?.orgId ? "none" : "active";
        queryClient.invalidateQueries([QUERY_NAME.ORGS], {
          refetchType,
        });
        queryClient.invalidateQueries([QUERY_NAME.ORG], {
          refetchType: "none",
        });
        if (!variables?.silent) {
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(error: any) {
        notification["error"]({
          message: handleMessageError(
            error?.response?.data?.msg || error?.response?.data?.error,
          ),
        });
      },
    },
  );
};

export const useSaveOrgs = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload, silent }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.ORGS_UPDATE_SORT_INDEX}`,
        data: {
          org_data: payload,
          company_id: companyId,
          current_company_id: companyId,
        },
      }),
    {
      onSuccess(_data, variables: any) {
        queryClient.invalidateQueries([QUERY_NAME.ORGS], {
          refetchType: "none",
        });
        if (!variables?.silent) {
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(error: any) {
        notification.error({
          message: getOrgErrorMessage(
            error?.response?.data?.msg || error?.response?.data?.error,
            "保存に失敗しました。",
          ),
        });
      },
    },
  );
};
